import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HeaderService } from '../../core/services/header.service';
import { Title } from '@angular/platform-browser';
import { BaseComponent } from '../../core/base.component';
import { AuthService } from '../../core/security/auth.service';
import { RolesEnum } from '../../core/security/roles.enum';
import { LocalPbxService } from '../local-pbx.service';
import { CurrentUser } from '../../api/models/current-user';
import { HttpErrorResponse } from '@angular/common/http';
import { PbxWallboardModel } from '../../api/models/pbx-wallboard-model';
import {
  CompactType,
  DisplayGrid,
  GridsterConfig,
  GridType,
  GridsterComponent,
  GridsterItemComponent,
} from 'angular-gridster2';
import { PbxWallboardLiveModel } from '../../api/models/pbx-wallboard-live-model';
import { WbDashboardItem } from './models/wb-dashboard-item';
import { EventsService } from '../../events/events.service';
import { PbxActiveCallModel } from '../../api/models/pbx-active-call-model';
import { WbBaseItem } from './models/wb-base-item';
import { FlexModule } from '@angular/flex-layout';
import { MatChipSet, MatChip } from '@angular/material/chips';
import { NgFor, NgIf } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { WbBaseItemComponent } from './widgets/wb-base-item.component';
import { DateUtils } from '../../core/utilities/date-time-helpers';
import { WallboardService } from './wallboard.service';

const dashboardStoreKey = 'wallboard-dashboard';

@Component({
  selector: 'app-pbx-wallboard',
  templateUrl: './wallboard.component.html',
  styleUrls: ['./wallboard.component.scss'],
  standalone: true,
  imports: [
    FlexModule,
    MatChipSet,
    NgFor,
    MatChip,
    NgIf,
    MatButton,
    MatTooltip,
    MatMenuTrigger,
    MatIcon,
    MatMenu,
    MatMenuItem,
    GridsterComponent,
    GridsterItemComponent,
    WbBaseItemComponent,
  ],
})
export class WallboardComponent extends BaseComponent implements OnInit, OnDestroy {
  detailsUrl: string;
  currentUser: CurrentUser;
  isLoading = false;
  hasError = false;
  errorMessage: string;

  isEditing = false;

  wallboards: PbxWallboardModel[] = [];
  selectedWallboard: PbxWallboardModel;
  activeGroupCalls: PbxActiveCallModel[] = [];
  liveData: PbxWallboardLiveModel;

  options: GridsterConfig;
  dashboard: Array<WbDashboardItem>;

  defaultDashboard: Array<WbDashboardItem> = [
    {
      cols: 1,
      rows: 1,
      y: 0,
      x: 0,
      item: {
        id: 'totalCalls',
        title: 'Total',
        icon: 'phone',
        type: 'counter',
        headerColor: '#EBEDF8',
        data: { value: 0 },
      },
    },
    {
      cols: 1,
      rows: 1,
      y: 0,
      x: 1,
      item: {
        id: 'totalIn',
        title: 'Answered',
        icon: 'call_received',
        type: 'counter',
        headerColor: '#C7FFEB',
        data: { value: 0 },
      },
    },
    {
      cols: 1,
      rows: 1,
      y: 0,
      x: 2,
      item: {
        id: 'totalMissed',
        title: 'Missed',
        icon: 'call_missed',
        type: 'counter',
        headerColor: '#FFC3C3',
        data: { value: 0 },
      },
    },
    {
      cols: 1,
      rows: 1,
      y: 0,
      x: 2,
      item: {
        id: 'totalHangup',
        title: 'Hangup',
        icon: 'call_missed',
        type: 'counter',
        headerColor: '#FFC3C3',
        data: { value: 0 },
      },
    },
    {
      cols: 1,
      rows: 1,
      y: 0,
      x: 3,
      item: {
        id: 'avgWaitTime',
        title: 'Avg. Wait Time',
        icon: 'timer',
        type: 'duration',
        headerColor: '#FBDAAD',
        data: { duration: 0 },
      },
    },
    {
      cols: 1,
      rows: 1,
      y: 0,
      x: 4,
      item: {
        id: 'maxWaitTime',
        title: 'Max. Wait Time',
        icon: 'schedule',
        type: 'duration',
        headerColor: '#FBDAAD',
        data: { duration: 0 },
      },
    },
    {
      cols: 3,
      rows: 3,
      y: 1,
      x: 0,
      item: {
        id: 'queueAgents',
        title: 'Agents',
        icon: 'support_agent',
        type: 'queueAgents',
        headerColor: '#EBEDF8',
        data: { duration: 0 },
      },
    },
    {
      cols: 1,
      rows: 3,
      y: 1,
      x: 0,
      item: {
        id: 'waitingCalls',
        title: 'Calls Waiting',
        icon: 'timelapse',
        type: 'liveCalls',
        headerColor: '#EBEDF8',
        data: { calls: [], type: 'waiting', queue: '' },
      },
    },
    {
      cols: 1,
      rows: 3,
      y: 1,
      x: 0,
      item: {
        id: 'connectedCalls',
        title: 'Connected Calls',
        icon: 'phone_in_talk',
        type: 'liveCalls',
        headerColor: '#EBEDF8',
        data: { calls: [], type: 'active', queue: '' },
      },
    },
  ];

  availableWallboardItems: WbBaseItem[] = [];

  userExtension: string;
  waitForCallsCounter = 0;
  callsIdsHash: string;
  liveDataRefreshTimeOutId: any;
  liveDataDebounceTimeOutId: any;
  readonly liveDataDebounceMs = 250;
  private pendingLiveDataResolvers: Array<() => void> = [];
  private isLiveDataFetchInProgress = false;
  activeCalls: Map<string, boolean> = new Map<string, boolean>();
  queueCalls: Map<string, boolean> = new Map<string, boolean>();

  wallboardService: WallboardService = inject(WallboardService);

  constructor(
    route: ActivatedRoute,
    headerService: HeaderService,
    titleService: Title,
    public authService: AuthService,
    public pbxService: LocalPbxService,
    public eventsService: EventsService,
  ) {
    super(route, headerService, titleService, authService);

    this.editRoles = [RolesEnum.Fax_Edit, RolesEnum.Fax_Delete];
    this.deleteRoles = [RolesEnum.Fax_Delete];

    this.availableWallboardItems = this.defaultDashboard
      .map(item => {
        return {
          id: item.item.id,
          title: item.item.title,
          icon: item.item.icon,
          type: item.item.type,
          headerColor: item.item.headerColor,
          data: item.item.data,
        };
      })
      .sort((a, b) => {
        return a.title.localeCompare(b.title);
      });
  }

  onCurrentUserChanged(user?: CurrentUser) {
    super.onCurrentUserChanged(user);

    if (user) {
      this.userExtension = user.primaryExtension;
    } else {
      this.userExtension = '';
    }
  }

  ngOnInit() {
    super.ngOnInit();

    this.loadDashboard().then(() => {
      this.eventsService.connectToEventsFlowRoom();
      this.wallboardService.triggerActiveDomainCalls().then();

      this.eventsService.subscribeEvents('event-flows', (event, data: any) => {
        console.log('event', event, data);
        if (
          this.selectedWallboard &&
          data.callType === 'telego-cdr' &&
          (data.extensions || []).includes(this.selectedWallboard?.name)
        ) {
          console.log('telego-cdr event received', data);
          this.getWallboardLiveData().then();
        }
      });

      this.eventsService.connectEmitter.subscribe(() => {
        this.eventsService.connectToEventsFlowRoom();
      });

      this.currentUser = this.authService.currentUser.getValue();

      this.refreshLiveData().then();
    });

    this.options = {
      gridType: GridType.Fit,
      compactType: CompactType.None,
      margin: 10,
      outerMargin: true,
      outerMarginTop: 10,
      outerMarginRight: 10,
      outerMarginBottom: 10,
      outerMarginLeft: 10,
      useTransformPositioning: true,
      mobileBreakpoint: 640,
      useBodyForBreakpoint: false,
      minCols: 3,
      maxCols: 100,
      minRows: 3,
      maxRows: 100,
      maxItemCols: 100,
      minItemCols: 1,
      maxItemRows: 100,
      minItemRows: 1,
      maxItemArea: 2500,
      minItemArea: 1,
      defaultItemCols: 1,
      defaultItemRows: 1,
      fixedColWidth: 105,
      fixedRowHeight: 105,
      keepFixedHeightInMobile: false,
      keepFixedWidthInMobile: false,
      scrollSensitivity: 10,
      scrollSpeed: 20,
      enableEmptyCellClick: false,
      enableEmptyCellContextMenu: false,
      enableEmptyCellDrop: false,
      enableEmptyCellDrag: false,
      enableOccupiedCellDrop: false,
      emptyCellDragMaxCols: 50,
      emptyCellDragMaxRows: 50,
      ignoreMarginInRow: false,
      draggable: {
        enabled: false,
      },
      resizable: {
        enabled: false,
      },
      swap: false,
      pushItems: true,
      disablePushOnDrag: false,
      disablePushOnResize: false,
      pushDirections: { north: true, east: true, south: true, west: true },
      pushResizeItems: false,
      displayGrid: DisplayGrid.None,
      disableWindowResize: false,
      disableWarnings: false,
      scrollToNewItems: false,
    };

    console.log('on init');
  }

  async loadDashboard() {
    const storedDashboard = localStorage.getItem(dashboardStoreKey);

    if (storedDashboard) {
      this.dashboard = JSON.parse(storedDashboard);
    } else {
      this.dashboard = this.copyDashboardObject(this.defaultDashboard);
    }

    console.log(this.dashboard);
  }

  async getWallBoards() {
    this.isLoading = true;
    this.hasError = false;
    try {
      this.wallboards = await this.pbxService.getWallboards();

      if (!this.selectedWallboard && this.wallboards.length > 0) {
        this.onWallboardClick(this.wallboards[0]);
      } else {
        this.getWallboardLiveData().then();
      }
    } catch (e) {
      this.handleError(e);
    } finally {
      this.isLoading = false;
    }
  }

  getWallboardLiveData(): Promise<void> {
    return new Promise(resolve => {
      this.pendingLiveDataResolvers.push(resolve);

      if (this.isLiveDataFetchInProgress) {
        return;
      }

      if (this.liveDataDebounceTimeOutId) {
        clearTimeout(this.liveDataDebounceTimeOutId);
      }

      this.liveDataDebounceTimeOutId = setTimeout(() => {
        this.liveDataDebounceTimeOutId = null;
        this.fetchWallboardLiveDataNow().finally(() => {
          const resolvers = [...this.pendingLiveDataResolvers];
          this.pendingLiveDataResolvers = [];
          resolvers.forEach(fn => fn());
        });
      }, this.liveDataDebounceMs);
    });
  }

  private async fetchWallboardLiveDataNow() {
    this.isLiveDataFetchInProgress = true;
    this.isLoading = true;
    this.hasError = false;

    try {
      if (this.selectedWallboard) {
        this.liveData = await this.pbxService.getWallboardLiveData(this.selectedWallboard.name);
        this.updateDashboard();
      }
    } catch (e) {
      this.handleError(e);
    } finally {
      this.isLiveDataFetchInProgress = false;
      this.isLoading = false;
    }
  }

  async getActiveGroupCalls() {
    if (this.liveDataRefreshTimeOutId) {
      clearTimeout(this.liveDataRefreshTimeOutId);
    }

    try {
      if (this.selectedWallboard) {
        const activeGroupCalls = await this.pbxService.getActiveGroupCalls(this.selectedWallboard.name);
        const callsIdsHash = activeGroupCalls.calls.map(x => `${x.id}|${x.state}`).join(',');
        let updateStatisticalData = false;

        console.log(`activeGroupCalls: wait: ${this.waitForCallsCounter}`, callsIdsHash, activeGroupCalls);

        if (this.callsIdsHash !== callsIdsHash) {
          console.log(`need to update: callsIdsHash: ${this.callsIdsHash} | ${callsIdsHash}`);
          updateStatisticalData = true;
        }

        this.callsIdsHash = callsIdsHash;
        this.activeGroupCalls = activeGroupCalls.calls;

        if (updateStatisticalData) {
          this.getWallboardLiveData().then();
        } else {
          this.updateDashboard();
        }

        if (this.activeGroupCalls.length == 0) {
          this.waitForCallsCounter++;
        }

        if (this.waitForCallsCounter < 60 && this.activeCalls.size > 0) {
          //this.getActiveGroupCallsWithTimeout();
        }
      }
    } catch (e) {
      this.handleError(e);
    }
  }

  getActiveGroupCallsWithTimeout() {
    this.liveDataRefreshTimeOutId = setTimeout(() => {
      this.getActiveGroupCalls().then();
    }, 1000);
  }

  async refreshLiveData() {
    this.isLoading = true;
    this.hasError = false;
    try {
      await this.getWallBoards();
    } catch (e) {
      this.handleError(e);
    } finally {
      this.isLoading = false;
    }
  }

  handleError(err) {
    this.hasError = true;
    const generalMessage = 'Error occur while trying to login, try again later or check your settings';
    this.errorMessage = generalMessage;
    if (err instanceof HttpErrorResponse) {
      this.errorMessage = err.error.message || generalMessage;
    }
  }

  ngOnDestroy() {
    this.eventsService.disconnectFromEventsFlowRoom();
    if (this.liveDataRefreshTimeOutId) {
      clearTimeout(this.liveDataRefreshTimeOutId);
    }
    if (this.liveDataDebounceTimeOutId) {
      clearTimeout(this.liveDataDebounceTimeOutId);
    }
    const resolvers = [...this.pendingLiveDataResolvers];
    this.pendingLiveDataResolvers = [];
    resolvers.forEach(fn => fn());
    super.ngOnDestroy();
  }

  onWallboardClick(item: PbxWallboardModel) {
    this.selectedWallboard = item;
    this.wallboardService.setCurrentQueue(item.name);
    // this.wallboardService.triggerActiveDomainCalls().then();
    this.getWallboardLiveData().then();
  }

  toggleFullScreen(enter: boolean): void {
    if (enter && !document.fullscreenElement) {
      document.documentElement.requestFullscreen?.().then();
    } else {
      document?.exitFullscreen?.().then();
    }
  }

  private updateDashboard() {
    this.dashboard.forEach(item => {
      if (item.item.id === 'totalCalls') {
        item.item.data = { value: this.liveData.queueStats.totalIncoming ?? 0 };
      } else if (item.item.id === 'totalIn') {
        item.item.data = { value: this.liveData.queueStats.totalAnswered ?? 0 };
      } else if (item.item.id === 'totalMissed') {
        item.item.data = {
          value: this.liveData.queueStats.totalMissed ?? 0,
        };
      } else if (item.item.id === 'totalHangup') {
        item.item.data = {
          value: this.liveData.queueStats.totalHangup ?? 0,
        };
      } else if (item.item.id === 'avgWaitTime') {
        item.item.data = {
          duration: this.liveData.queueStats.avgIvrDuration + this.liveData.queueStats.avgRingDuration,
        };
      } else if (item.item.id === 'maxWaitTime') {
        item.item.data = {
          duration: this.liveData.queueStats.maxIvrDuration + this.liveData.queueStats.maxRingDuration,
        };
      } else if (item.item.id === 'queueAgents') {
        item.item.data = {
          userExtension: this.userExtension,
          queueData: this.liveData.queue,
          isManager: this.liveData.queue.managers.includes(this.userExtension),
          groupId: this.selectedWallboard?.name,
          agents: this.liveData.agents,
          activeCalls: this.liveData.current,
          totalAgents: this.liveData.agents?.length,
          loggedInAgents: this.liveData.agents?.filter(x => x.login).length,
          workHoursStart: DateUtils.minutesToTimeSpanString(this.liveData.queue.workHoursStart, '12'),
          workHoursEnd: DateUtils.minutesToTimeSpanString(this.liveData.queue.workHoursEnd, '12'),
        };
      } else if (item.item.id === 'waitingCalls') {
        item.item.data = {
          queue: this.selectedWallboard?.name,
          agents: this.liveData.agents,
          calls: this.liveData.current.filter(x => !x.agent),
          type: 'waiting',
        };
      } else if (item.item.id === 'connectedCalls') {
        item.item.data = {
          queue: this.selectedWallboard?.name,
          agents: this.liveData.agents,
          calls: this.liveData.current.filter(x => x.agent),
          type: 'active',
        };
      }
    });
  }

  saveWallboard() {
    this.isEditing = false;

    const newOptions = {};

    Object.assign(newOptions, this.options, {
      draggable: {
        enabled: false,
      },
      resizable: {
        enabled: false,
      },
      displayGrid: DisplayGrid.None,
    });

    this.options = newOptions;

    localStorage.setItem(dashboardStoreKey, JSON.stringify(this.dashboard));
  }

  restoreWallboard() {
    this.isEditing = false;

    const newOptions = {};

    Object.assign(newOptions, this.options, {
      draggable: {
        enabled: false,
      },
      resizable: {
        enabled: false,
      },
      displayGrid: DisplayGrid.None,
    });

    this.options = newOptions;

    // Restore the default dashboard
    const defaultDashboard = this.defaultDashboard.filter(item => item.item.id !== 'totalHangup');
    this.dashboard = this.copyDashboardObject(defaultDashboard);

    localStorage.removeItem(dashboardStoreKey);
    this.updateDashboard();
  }

  cancelEditWallboard() {
    this.isEditing = false;

    const newOptions = {};

    Object.assign(newOptions, this.options, {
      draggable: {
        enabled: false,
      },
      resizable: {
        enabled: false,
      },
      displayGrid: DisplayGrid.None,
    });

    this.options = newOptions;

    this.loadDashboard().then();
    this.updateDashboard();
  }

  startEditWallboard() {
    this.isEditing = true;

    const newOptions = {};

    Object.assign(newOptions, this.options, {
      draggable: {
        enabled: this.isEditing,
      },
      resizable: {
        enabled: this.isEditing,
      },
      displayGrid: this.isEditing ? DisplayGrid.Always : DisplayGrid.None,
    });

    this.options = newOptions;
  }

  onItemRemove($event: WbBaseItem) {
    if (this.isEditing) {
      this.dashboard = this.dashboard.filter(item => item.item.id !== $event.id);
    }
  }

  addWidget(item: WbBaseItem) {
    if (this.isEditing) {
      const newItem = {
        cols: 1,
        rows: 1,
        y: 0,
        x: 0,
        item: {
          id: item.id,
          title: item.title,
          icon: item.icon,
          type: item.type,
          headerColor: item.headerColor,
          data: item.data,
        },
      };

      this.dashboard.push(newItem);
    }
  }

  getAvailableWallboardItems() {
    const itemIdsInWallboard = this.dashboard.map(item => item.item.id);
    return this.availableWallboardItems.filter(item => {
      return itemIdsInWallboard.indexOf(item.id) === -1;
    });
  }

  copyDashboardObject(dashboard: Array<WbDashboardItem>) {
    const jsonString = JSON.stringify(dashboard);
    return JSON.parse(jsonString);
  }
}
